import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getHeaders } from "@/contexts/AuthContext";
import EmployeesCard from "./components/EmployeesCard";
import { Integration } from "@/api/IntegrationsAPI";

interface EmployeesTabProps {
  propertyId: string;
  currentIntegration?: Integration | null;
}

interface EmployeeType {
  id: string;
  typeName: string;
}

const EmployeesTab: React.FC<EmployeesTabProps> = ({ propertyId, currentIntegration = null }) => {
  const [types, setTypes] = useState<EmployeeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const headers = await getHeaders();
      // Include propertyId as query param to scope types to this property if supported
      const url = `${import.meta.env.VITE_API_URL}/payroll/employeeTypes${propertyId ? `?propertyId=${propertyId}` : ""}`;
      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) throw new Error("Failed to fetch employee types");
      const data: EmployeeType[] = await res.json();
      setTypes(data || []);
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Could not load employee types.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, [propertyId]);

  const handleCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newTypeName.trim()) return;
    setSubmitting(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${import.meta.env.VITE_API_URL}/payroll/employeeTypes`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId, typeName: newTypeName.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create employee type");
      }

      toast({ title: "Saved", description: "Employee type created." });
      setNewTypeName("");
      await fetchTypes();
    } catch (err: any) {
      console.error(err);
      toast({ title: "Error", description: err.message || "Failed to save employee type.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <EmployeesCard propertyId={propertyId} currentIntegration={currentIntegration} employeeTypes={types} />
      <Card>
        <CardHeader>
          <CardTitle>Employee Types</CardTitle>
          <CardDescription>Manage the list of employee types for this property.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : types.length > 0 ? (
            <div className="space-y-2">
              {types.map((t) => (
                <div key={t.id} className="flex justify-between items-center border-b py-2">
                  <div>
                    <p className="font-medium">{t.typeName}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No employee types found.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create Employee Type</CardTitle>
          <CardDescription>Add a new employee type for this property.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="typeName">Type Name</Label>
              <Input id="typeName" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="e.g. Housekeeping" />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Saving..." : "Create Type"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeesTab;
